// R-167 WU-6a — /admin/counters list page.
//
// Renders all CustomCounter rows for the tenant. Each row exposes Edit /
// Reset / Delete actions. Delete may surface a 409 COUNTER_IN_USE — the
// page catches that and opens CounterDeleteGuardModal with the references
// payload instead of toasting "couldn't delete."
import { useState } from "react";
import { AdminListHeader } from "@/components/admin/AdminListHeader";
import { ApiError } from "@/lib/api-client";
import { showApiError } from "@/lib/error-toast";
import { useJobTypes } from "@/features/admin/hooks/useJobTypes";
import { useCustomCounters } from "../hooks/useCustomCounters";
import {
  countersApi,
  type CustomCounter,
  type CounterReference,
} from "../api/client";
import CounterEditorModal, {
  type CounterEditorSubmitPayload,
} from "../components/CounterEditorModal";
import CounterDeleteGuardModal from "../components/CounterDeleteGuardModal";
import styles from "../counters.module.css";

type EditorState =
  | { mode: "create" }
  | { mode: "edit"; counter: CustomCounter }
  | null;

interface DeleteBlockState {
  counterName: string;
  refs: CounterReference[];
}

export default function CountersListPage() {
  const { counters, loading, refetch } = useCustomCounters();
  const { items: jobTypes } = useJobTypes();
  const [editing, setEditing] = useState<EditorState>(null);
  const [deleteBlock, setDeleteBlock] = useState<DeleteBlockState | null>(null);

  async function handleSubmit(payload: CounterEditorSubmitPayload) {
    try {
      if (editing?.mode === "create") {
        await countersApi.create({
          name: payload.name,
          jobTypeId: payload.jobTypeId,
          minValue: payload.minValue,
          maxValue: payload.maxValue,
          paddingWidth: payload.paddingWidth,
          resetCadence: payload.resetCadence,
        });
      } else if (editing?.mode === "edit") {
        await countersApi.update(editing.counter.id, {
          minValue: payload.minValue,
          maxValue: payload.maxValue,
          paddingWidth: payload.paddingWidth,
          resetCadence: payload.resetCadence,
        });
      }
      setEditing(null);
      await refetch();
    } catch (e) {
      showApiError(e);
    }
  }

  async function handleReset(c: CustomCounter) {
    if (
      !window.confirm(
        `Reset counter '${c.name}'? Next allocation will return ${c.minValue}.`,
      )
    ) {
      return;
    }
    try {
      await countersApi.reset(c.id);
      await refetch();
    } catch (e) {
      showApiError(e);
    }
  }

  async function handleDelete(c: CustomCounter) {
    if (!window.confirm(`Delete counter '${c.name}'?`)) return;
    try {
      await countersApi.delete(c.id);
      await refetch();
    } catch (e) {
      // WU-5 surfaces a structured 409 when a template still references the
      // counter. Open the guard modal with the references list instead of
      // showing a generic error toast.
      if (
        e instanceof ApiError &&
        e.status === 409 &&
        typeof e.body === "object" &&
        e.body !== null &&
        (e.body as { code?: string }).code === "COUNTER_IN_USE"
      ) {
        const body = e.body as { references?: CounterReference[] };
        setDeleteBlock({
          counterName: c.name,
          refs: body.references ?? [],
        });
        return;
      }
      showApiError(e);
    }
  }

  const jobTypeOptions = jobTypes.map((j) => ({ id: j.id, name: j.name }));

  return (
    <div className={styles.listPage}>
      <AdminListHeader
        title="Custom Counters"
        count={counters.length}
        primaryAction={{
          label: "+ New counter",
          onClick: () => setEditing({ mode: "create" }),
        }}
      />

      {loading ? (
        <p className={styles.emptyState}>Loading…</p>
      ) : counters.length === 0 ? (
        <div className={styles.emptyState}>
          No counters configured yet.{" "}
          <button
            type="button"
            className={styles.primary}
            style={{ marginLeft: 12 }}
            onClick={() => setEditing({ mode: "create" })}
          >
            + New counter
          </button>
        </div>
      ) : (
        <table className={styles.counterTable}>
          <thead>
            <tr>
              <th>Name</th>
              <th>Scope</th>
              <th>Range</th>
              <th>Padding</th>
              <th>Reset</th>
              <th>Current</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {counters.map((c) => (
              <tr key={c.id}>
                <td>
                  <code>{c.name}</code>
                </td>
                <td>
                  {c.jobTypeId ? (
                    <span className={styles.scopeChipJobType}>Job Type</span>
                  ) : (
                    <span className={styles.scopeChipTenant}>Tenant</span>
                  )}
                </td>
                <td>
                  {c.minValue}–{c.maxValue}
                </td>
                <td>{c.paddingWidth > 0 ? c.paddingWidth : "—"}</td>
                <td>{c.resetCadence}</td>
                <td>
                  {c.currentValue}
                  {c.currentPeriodKey ? (
                    <>
                      {" "}
                      <small>· {c.currentPeriodKey}</small>
                    </>
                  ) : null}
                </td>
                <td>
                  {c.exhaustedAt ? (
                    <span className={styles.exhaustedBadge}>Exhausted</span>
                  ) : (
                    <span className={styles.statusOk}>● OK</span>
                  )}
                </td>
                <td>
                  <div className={styles.rowActions}>
                    <button
                      type="button"
                      onClick={() =>
                        setEditing({ mode: "edit", counter: c })
                      }
                    >
                      Edit
                    </button>
                    <button type="button" onClick={() => handleReset(c)}>
                      Reset
                    </button>
                    <button type="button" onClick={() => handleDelete(c)}>
                      Delete
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {editing && (
        <CounterEditorModal
          mode={editing.mode}
          initial={editing.mode === "edit" ? editing.counter : undefined}
          jobTypes={jobTypeOptions}
          onClose={() => setEditing(null)}
          onSubmit={handleSubmit}
        />
      )}

      {deleteBlock && (
        <CounterDeleteGuardModal
          counterName={deleteBlock.counterName}
          references={deleteBlock.refs}
          onClose={() => setDeleteBlock(null)}
        />
      )}
    </div>
  );
}
